import React, { useState } from 'react';
import PropTypes from 'prop-types';
import CanPerform from 'components/shared/CanPerform';
import { CHECKIN } from 'constants/afterSalesShortcuts';
import { FormattedMessage } from 'react-intl';
import { bookingShape } from 'constants/propTypes';
import Modal from 'components/ui/Modal';

import CheckinTicketsModal from 'components/shared/CheckinTicketsModal';

import { useManagedMutation } from 'utils/managedHooks';

import { Spinner } from 'admin-components/components';
import { Row, Column } from 'components/shared/Layout';
import { CHECKIN_TICKETS, CHECKIN_TICKETS_IN_SALESFORCE, ADD_ADDITIONAL_DETAILS } from '../../../../../store/mutations';
import { CtaButton } from '../AfterSalesCtas.styles';

import {
  runAfterSaleValidator,
  allSelectedProductsConfirmed,
  getTicketNumbers,
  bookingIsConfirmed,
  isDepartureBeforeToday,
  selectedItemsArePartOfASummatedFare,
  areSomeExternalCarrierProductsSelected
} from '../AfterSalesCtas.utils';

const CheckInCta = ({ booking, selectedItemRefs, disabled, isOutboundSummatedFare = false, isInboundSummatedFare = false }) => {
  const [enterReasonVisible, setEnterReasonVisible] = useState(false);
  const [ticketsNotUpdatedVisible, setTicketsNotUpdatedVisible] = useState(false);
  const [ticketsNotUpdated, setTicketsNotUpdated] = useState('');
  const [ticketsUpdated, setTicketsUpdated] = useState('');

  const [checkInTickets, { loading }] = useManagedMutation(CHECKIN_TICKETS);

  const [checkInTicketsInSalesforce, { loading: loadingCheckInTicketsInSalesforce }] = useManagedMutation(CHECKIN_TICKETS_IN_SALESFORCE, {
    refetchQueries: ['booking'],
    awaitRefetchQueries: true
  });

  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useManagedMutation(ADD_ADDITIONAL_DETAILS);

  const areItemsInASummatedFare = selectedItemsArePartOfASummatedFare(
    booking,
    selectedItemRefs,
    isOutboundSummatedFare,
    isInboundSummatedFare
  );

  const hasExternalCarriersSelected = areSomeExternalCarrierProductsSelected({
    selectedItemRefs,
    journeys: [...(booking.outbound || []), ...(booking.inbound || [])]
  });
  const isDisabled =
    disabled ||
    !runAfterSaleValidator(booking, selectedItemRefs)([bookingIsConfirmed, allSelectedProductsConfirmed]) ||
    !isDepartureBeforeToday(selectedItemRefs, booking) ||
    (areItemsInASummatedFare && hasExternalCarriersSelected);

  const ticketNumbers = getTicketNumbers({
    booking,
    selectedItemRefs
  });

  if (loading || addAdditionalDetailsLoading) {
    return (
      <Row span={16}>
        <Column justifyContent="center" alignItems="center">
          <Spinner type={'circular'} />
        </Column>
      </Row>
    );
  }

  const additionalDetailsPerTickets = ({ reason }) =>
    ticketNumbers.map((item) => {
      const product = [...booking.outbound, ...booking.inbound].find(({ products }) =>
        products.some(({ ticketNumber }) => ticketNumber === item)
      );
      const findRef = booking.allBookingItems.find((product) => product.ticketNumber === item);

      return {
        itemRefId: Number(findRef.itemRef),
        key: 'CheckIn_Reason',
        value: `${product?.serviceType?.code || 'ES'} ticket: ${item}, itemRef: ${findRef.itemRef}. Reason: ${reason}`
      };
    });

  const handleSubmit = async ({ reason }) => {
    await addAdditionalDetails({
      variables: {
        bookingReference: booking.reference,
        additionalDetails: additionalDetailsPerTickets({ reason })
      }
    });

    await checkInTickets({
      variables: {
        ticketNumbers,
        bookingReference: booking.reference
      }
    });

    const checkInResponse = await checkInTicketsInSalesforce({
      variables: {
        bookingReference: booking.reference,
        tcnList: ticketNumbers
      }
    });

    const updatedTickets = checkInResponse?.data?.checkInTicketsInSalesforce || [];
    const notUpdated = ticketNumbers.filter((ticket) => !updatedTickets.includes(ticket));
    const updated = ticketNumbers.filter((ticket) => updatedTickets.includes(ticket));

    if (notUpdated.length) {
      setTicketsNotUpdated(notUpdated.join(', '));
    }

    if (updated.length) {
      setTicketsUpdated(updated.join(', '));
    }

    if (notUpdated.length) {
      setTicketsNotUpdatedVisible(true);
      return;
    }

    setEnterReasonVisible(!enterReasonVisible);
  };

  return (
    <>
      {ticketsNotUpdatedVisible && (
        <Modal
          isOpen={ticketsNotUpdatedVisible}
          title={<FormattedMessage id="tickets.checkIn.sf.error" />}
          onClose={() => {
            setTicketsNotUpdatedVisible(!ticketsNotUpdatedVisible);
            setEnterReasonVisible(!enterReasonVisible);
          }}
        >
          <h4 data-testid="checkin-tickets-form-tickets" data-cy="checkin-tickets-form-tickets">
            <FormattedMessage id="tickets.checkIn.sf.error.title" />
          </h4>

          <p>
            <FormattedMessage id="tickets.checkIn.sf.error.updated" /> &nbsp;
            {ticketsUpdated}
          </p>

          <p>
            <FormattedMessage id="tickets.checkIn.sf.error.error" /> &nbsp;
            {ticketsNotUpdated}
          </p>
        </Modal>
      )}

      {enterReasonVisible && (
        <CheckinTicketsModal
          isOpen={enterReasonVisible}
          isLoading={loading || loadingCheckInTicketsInSalesforce}
          onToggle={() => setEnterReasonVisible(!enterReasonVisible)}
          onSubmit={handleSubmit}
          ticketNumbers={ticketNumbers}
        />
      )}
      <CanPerform afterSalesShortcut={CHECKIN}>
        {(isEligible) =>
          isEligible && (
            <CtaButton
              onClick={() => setEnterReasonVisible(true)}
              data-cy="checkin-cta"
              label={<FormattedMessage id="afterSales.checkin" />}
              disabled={isDisabled}
            />
          )
        }
      </CanPerform>
    </>
  );
};

CheckInCta.propTypes = {
  booking: bookingShape,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  isOutboundSummatedFare: PropTypes.bool,
  isInboundSummatedFare: PropTypes.bool
};

export default CheckInCta;
